import { useState } from 'react';
import { Box, Button, Center, Group, Loader, Modal, SimpleGrid, Stack, Text, TextInput, ThemeIcon } from '@mantine/core';
import { useAuth } from '@workos-inc/authkit-react';
import { SectionTitle } from './TitleSection';
import { IconEntitlements, IconUsers, PROFILE_URL } from '../../lib/profileNav';
import { IconArrowRight, type IconProps } from '@tabler/icons-react';
import { NavLink } from 'react-router-dom';
import { ActiveProductsStat, PendingInvitesStat, TotalUsersStat } from './ProfileOverviewStats';
import { useOrgStats } from './orgStats';
import { InlineNotice } from '../../components/InlineNotice';
import { api, bearer } from '../../api/client';

const ORG_DELETE = 'org-admin:org-delete';

// Maps the delete endpoint's documented error bodies to admin-facing text. Irreversible and
// non-retriable on partial failure (see stuck-organisation-delete-reconciliation.md), so a
// half-done delete must be surfaced clearly rather than swallowed into a generic message.
function deleteErrorMessage(status: number, error: string | undefined): string {
  switch (error) {
    case 'org_mismatch':
    case 'org_not_found':
      return "That doesn't match your organisation. Nothing was deleted.";
    case 'org_protected':
      return 'You may not delete the Cloudhouse organisation.';
    case 'workos_failed':
      return 'Deleted locally, but removing the WorkOS organisation failed. Contact Cloudhouse support — this needs manual reconciliation.';
    case 'teardown_failed':
      return 'Deleted, but tenant teardown did not trigger. Contact Cloudhouse support — this needs manual reconciliation.';
    case 'audit_unavailable':
      return 'Your organisation was deleted, but the audit record failed to write. Contact Cloudhouse support to record this.';
    default:
      return `Delete failed (${status || 'network'}). Nothing was changed.`;
  }
}

type Action = {
  title: string;
  subtitle: string;
  to: string;
  Icon: React.ComponentType<IconProps>;
}

const pageData = {
  title: 'Overview',
  subtitle: 'A snapshot of your organisation\'s Cloudhouse usage and administration.',
}


export function ProfileOverview() {
  const { loading, error, users, products } = useOrgStats();
  const auth = useAuth();
  const canDeleteOrg = (auth.permissions ?? []).includes(ORG_DELETE);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function deleteOwnOrganisation() {
    const orgId = auth.organizationId;
    if (!orgId) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const token = await auth.getAccessToken();
      if (!token) {
        setDeleteError('Your session has expired. Refresh to sign in again; nothing was changed.');
        return;
      }
      const { error: apiError, response } = await api.DELETE('/organisations/{orgId}', {
        params: { path: { orgId } },
        headers: bearer(token),
      });
      if (apiError) {
        const body = apiError as { error?: string } | undefined;
        setDeleteError(deleteErrorMessage(response?.status ?? 0, body?.error));
        return;
      }
      // The organisation is gone — nothing in the app works for this session anymore.
      auth.signOut();
    } catch {
      setDeleteError('Delete failed (network). Nothing was changed.');
    } finally {
      setDeleteBusy(false);
    }
  }

  const quickActions: Action[] = [
    {
      title: 'Manage users',
      subtitle: 'Invite, suspend & reactivate',
      to: `${PROFILE_URL}/users`,
      Icon: IconUsers,
    },
    {
      title: 'Entitlements',
      subtitle: 'Licensed products & status',
      to: `${PROFILE_URL}/entitlements`,
      Icon: IconEntitlements,
    },
  ];



  return (
    <Stack flex={1} style={{ overflowY: 'auto' }} px={32} py={24} data-testid="profile-overview-tab" gap={0}>
      <SectionTitle {...pageData} />
      {loading && <Center py={40}><Loader size="sm" /></Center>}
      {!loading && error && (
        <InlineNotice tone="error" message="Couldn't load overview stats — check the entitlements API is running." />
      )}
      {!loading && !error && users && products && (
        <SimpleGrid data-testid="profile-overview-stats" cols={{ base: 1, sm: 2, lg: 3 }} spacing={16} mt="22">
          <TotalUsersStat stats={users} />
          <ActiveProductsStat stats={products} />
          <PendingInvitesStat stats={users} />
        </SimpleGrid>
      )}
      <Stack mt={28}>
        <Text lts={'.5px'} fz={'xs'} fw={700} c={'var(--text-subtle)'} lh={'normal'} tt={'uppercase'}>Quick actions</Text>
        <SimpleGrid cols={2} spacing={16}>
          {quickActions.map((action) => (

            <Button variant='transparent' h={'auto'} p={'18px 20px'} data-product-tile={true} key={action.title} component={NavLink} to={action.to}
              style={{
                '--transform': '-2px',
                '--shadow-hover': 'var(--shadow-md)',
                '--button-color': 'var(--text-subtle)',
                '--button-hover-color': 'var(--accent-text)',
                '--border-hover': 'var(--brand-soft-bd)',
                background: 'var(--bg-surface)',
              }}
              styles={{ label: { width: '100%' } }}
            >
              <Group wrap='nowrap' align='center' gap={12} w={'100%'} ta={'left'} >
                <ThemeIcon bd={'1px solid var(--brand-soft-bd)'} flex={'none'} size={42} c={'var(--accent-text)'} bg={'var(--brand-soft)'}>
                  <action.Icon size={18} />
                </ThemeIcon>
                <Box flex={1}>
                  <Text fz={'sm'} fw={700} c={'var(--text-default)'} lh={'normal'}>{action.title}</Text>
                  <Text fz={'xs'} c={'var(--text-muted)'}>{action.subtitle}</Text>
                </Box>
                <IconArrowRight size={18} />
              </Group>
            </Button>
          ))}
        </SimpleGrid>
      </Stack>

      {canDeleteOrg && (
        <Stack mt={28} gap={14} p={20} data-testid="danger-zone"
          style={{ border: '1px solid var(--status-danger)', borderRadius: 12 }}>
          <Text fz="xs" fw={700} c="var(--status-danger)" lts=".5px" tt="uppercase">Danger zone</Text>
          <Group justify="space-between" align="center" wrap="nowrap">
            <Box>
              <Text fz="sm" fw={600}>Delete this organisation</Text>
              <Text fz="xs" c="var(--text-muted)">
                Permanently deletes your organisation, its users, and every licensed product. This cannot be undone.
              </Text>
            </Box>
            <Button color="red" variant="outline"
              onClick={() => { setConfirmText(''); setDeleteError(null); setConfirmingDelete(true); }}>
              Delete organisation
            </Button>
          </Group>
        </Stack>
      )}

      <Modal opened={confirmingDelete}
        onClose={() => { if (!deleteBusy) setConfirmingDelete(false); }}
        title="Delete organisation?" centered
        styles={{
          header: { position: 'relative' },
          title: { flex: 1, textAlign: 'center' },
          close: { position: 'absolute', right: 'var(--mantine-spacing-md)' },
        }}>
        <Stack gap="md">
          <Text fz="sm" fw={600} c="var(--status-danger)">
            This cannot be undone.
          </Text>
          <Text fz="sm">
            Deletes your organisation, its users, and all licensed products. To confirm, type{' '}
            <Text component="span" fz="sm" fw={600} ff="monospace">DELETE</Text> below.
          </Text>
          <TextInput
            value={confirmText}
            onChange={(e) => setConfirmText(e.currentTarget.value)}
            placeholder="Type DELETE"
            aria-label="Type DELETE to confirm deletion"
            disabled={deleteBusy}
          />
          {deleteError && <InlineNotice tone="error" message={deleteError} onClose={() => setDeleteError(null)} />}
          <Group justify="flex-end" gap={10}>
            <Button variant="default" disabled={deleteBusy} onClick={() => setConfirmingDelete(false)}>
              Cancel
            </Button>
            <Button color="red" loading={deleteBusy}
              disabled={!auth.organizationId || confirmText.trim() !== 'DELETE'}
              onClick={() => void deleteOwnOrganisation()}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack >
  );
}
